import React, { useState, useCallback, useEffect } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { MapPin, Navigation, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

const mapContainerStyle = {
  width: "100%",
  height: "260px",
  borderRadius: "12px",
};

const defaultCenter = { lat: 26.8467, lng: 80.9462 };

export interface GeocodedLocationData {
  lat: number;
  lng: number;
  address: string;
  city: string;
  state: string;
  pincode: string;
}

interface MapLocationPickerProps {
  initialCenter?: { lat: number; lng: number } | null;
  onLocationSelect: (data: GeocodedLocationData) => void;
}

export const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  initialCenter,
  onLocationSelect,
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markerPos, setMarkerPos] = useState<{ lat: number; lng: number }>(
    initialCenter && initialCenter.lat && initialCenter.lng
      ? initialCenter
      : defaultCenter
  );
  const [geocoding, setGeocoding] = useState(false);
  const [loadingCurrentLocation, setLoadingCurrentLocation] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      process.env.GOOGLE_MAPS_API_KEY ||
      "",
  });

  // Reverse geocode lat/lng to get address components
  const reverseGeocode = useCallback(
    (lat: number, lng: number) => {
      if (!window.google || !window.google.maps) return;
      const geocoder = new window.google.maps.Geocoder();
      setGeocoding(true);

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        setGeocoding(false);
        if (status === "OK" && results && results[0]) {
          const res = results[0];
          let fullAddress = res.formatted_address || "";
          let city = "";
          let state = "";
          let pincode = "";

          res.address_components.forEach((comp) => {
            if (comp.types.includes("locality")) city = comp.long_name;
            else if (comp.types.includes("administrative_area_level_2") && !city)
              city = comp.long_name;

            if (comp.types.includes("administrative_area_level_1"))
              state = comp.long_name;

            if (comp.types.includes("postal_code")) pincode = comp.long_name;
          });

          onLocationSelect({
            lat,
            lng,
            address: fullAddress,
            city,
            state,
            pincode,
          });
        } else {
          onLocationSelect({
            lat,
            lng,
            address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
            city: "",
            state: "",
            pincode: "",
          });
        }
      });
    },
    [onLocationSelect]
  );

  useEffect(() => {
    if (initialCenter && initialCenter.lat && initialCenter.lng) {
      setMarkerPos(initialCenter);
      if (map) {
        map.panTo(initialCenter);
        map.setZoom(16);
      }
    }
  }, [initialCenter, map]);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    setMarkerPos({ lat: newLat, lng: newLng });
    reverseGeocode(newLat, newLng);
  };

  const handleMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    setMarkerPos({ lat: newLat, lng: newLng });
    reverseGeocode(newLat, newLng);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setLoadingCurrentLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newPos = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setMarkerPos(newPos);
        if (map) {
          map.panTo(newPos);
          map.setZoom(16);
        }
        setLoadingCurrentLocation(false);
        reverseGeocode(newPos.lat, newPos.lng);
      },
      (err) => {
        setLoadingCurrentLocation(false);
        toast.error("Unable to get current location");
      },
      { timeout: 8000 }
    );
  };

  if (!isLoaded) {
    return (
      <div style={mapContainerStyle} className="bg-slate-100 flex items-center justify-center border border-slate-200">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
          <Loader2 size={16} className="animate-spin text-emerald-500" />
          Loading Google Map...
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-xs">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={markerPos}
        zoom={15}
        onLoad={(mapInstance) => setMap(mapInstance)}
        onClick={handleMapClick}
        options={{
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          zoomControl: true,
        }}
      >
        <Marker
          position={markerPos}
          draggable
          onDragEnd={handleMarkerDragEnd}
        />
      </GoogleMap>

      {/* Controls Overlay */}
      <div className="absolute top-2.5 right-2.5 flex items-center gap-2 z-10">
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          disabled={loadingCurrentLocation}
          className="px-3 py-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 shadow-md text-xs font-bold text-slate-700 flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
        >
          {loadingCurrentLocation ? (
            <Loader2 size={13} className="animate-spin text-emerald-600" />
          ) : (
            <Navigation size={13} className="text-emerald-600" />
          )}
          My Location
        </button>
      </div>

      {geocoding && (
        <div className="absolute bottom-2.5 left-2.5 px-3 py-1.5 rounded-lg bg-slate-900/80 text-white text-[0.7rem] font-bold flex items-center gap-1.5 shadow-md">
          <Loader2 size={12} className="animate-spin" /> Fetching location details...
        </div>
      )}
    </div>
  );
};

export default MapLocationPicker;
